package org.chiangmeng.qfast.core.data.remote

import dev.gitlive.firebase.database.DataSnapshot
import dev.gitlive.firebase.database.DatabaseReference
import dev.gitlive.firebase.database.FirebaseDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import kotlin.time.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.serializer
import org.chiangmeng.qfast.core.domain.models.QueueTicket
import org.chiangmeng.qfast.core.domain.models.Shop
import org.chiangmeng.qfast.core.domain.models.ShopFeatures
import org.chiangmeng.qfast.core.domain.models.QueueHistoryItem
import org.chiangmeng.qfast.core.domain.ports.QueueRepository
import org.chiangmeng.qfast.database.QueueHistoryQueries

class FirebaseQueueRepositoryImpl(
    private val firebaseDatabase: FirebaseDatabase,
    private val historyQueries: QueueHistoryQueries
) : QueueRepository {

    private val nextQueueMutex = Mutex()
    private val localQueuesFlow = MutableStateFlow<List<QueueTicket>>(emptyList())
    private val localShopStateTrigger = MutableStateFlow(0L)
    private val shopPlanCache = mutableMapOf<String, String>()

    // MARK: - Internal Helpers
    private suspend fun isShopPro(shopId: String): Boolean {
        shopPlanCache[shopId]?.let { return it == "PRO" }
        return try {
            val snap = firebaseDatabase.reference("shops_static/$shopId/plan").valueEvents.first()
            val plan = snap.value<String?>()?.takeIf { it.isNotBlank() && it != "<null>" && it != "null" } ?: "FREE"
            shopPlanCache[shopId] = plan
            plan == "PRO"
        } catch (_: Throwable) {
            false
        }
    }

    private fun refreshLocalQueues(shopId: String) {
        val entities = historyQueries.selectActiveQueuesByShopId(shop_id = shopId).executeAsList()
        localQueuesFlow.value = entities.map { entity ->
            QueueTicket(
                number = entity.ticket_number.toInt(),
                category = entity.category,
                status = entity.status,
                time = entity.time,
                callTime = entity.call_time,
                preOrder = entity.pre_order
            )
        }
    }

    private fun getLocalCurrentServings(shopId: String): Map<String, Int> {
        val json = historyQueries.selectShopState(shop_id = shopId).executeAsOneOrNull()?.current_servings_json ?: return emptyMap()
        if (json.isBlank()) return emptyMap()
        return json.split(",")
            .mapNotNull { item ->
                val parts = item.split(":")
                if (parts.size == 2) parts[0].trim() to (parts[1].trim().toIntOrNull() ?: 0) else null
            }
            .toMap()
    }

    private fun saveLocalCurrentServings(shopId: String, servings: Map<String, Int>) {
        val str = servings.entries.joinToString(",") { "${it.key}:${it.value}" }
        val state = historyQueries.selectShopState(shop_id = shopId).executeAsOneOrNull()
        if (state == null) {
            historyQueries.insertOrReplaceShopState(shop_id = shopId, last_ticket = 0L, current_servings_json = str)
        } else {
            historyQueries.updateCurrentServingsJson(current_servings_json = str, shop_id = shopId)
        }
    }

    // MARK: - Shop Observation
    override fun observeShop(shopId: String): Flow<Shop?> {
        val staticRef: DatabaseReference = firebaseDatabase.reference("shops_static/$shopId")
        val liveRef: DatabaseReference = firebaseDatabase.reference("shops_live/$shopId")

        return combine(staticRef.valueEvents, liveRef.valueEvents, localShopStateTrigger) { staticSnap, liveSnap, _ ->
            if (!staticSnap.exists) {
                println("QFAST_DEBUG: ❌ Snapshot shops_static/$shopId NOT FOUND in Firebase")
                return@combine null
            }

            val idValue = try {
                staticSnap.child("id").value<String?>() ?: shopId
            } catch (t: Throwable) {
                println("QFAST_DEBUG: ❌ Failed field 'id': $t")
                shopId
            }

            val nameValue = try {
                staticSnap.child("name").value<String?>() ?: "QFast Shop"
            } catch (t: Throwable) {
                println("QFAST_DEBUG: ❌ Failed field 'name': $t")
                "QFast Shop"
            }

            val planValue = try {
                staticSnap.child("plan").value<String?>()
                    ?.takeIf { it.isNotBlank() && it != "<null>" && it != "null" } ?: "FREE"
            } catch (_: Throwable) {
                "FREE"
            }
            shopPlanCache[shopId] = planValue

            val isPro = planValue == "PRO"

            val currentServingsMap = if (isPro) {
                try {
                    val cs = liveSnap.child("current_servings")
                    if (cs.exists) {
                        cs.children.associate { child ->
                            (child.key ?: "") to (child.value<Long?>()?.toInt() ?: 0)
                        }.filterKeys { it.isNotBlank() }
                    } else {
                        mapOf("A" to 0, "B" to 0, "C" to 0)
                    }
                } catch (t: Throwable) {
                    println("QFAST_DEBUG: ❌ Failed field 'current_servings': $t")
                    mapOf("A" to 0, "B" to 0, "C" to 0)
                }
            } else {
                val localServings = getLocalCurrentServings(shopId)
                if (localServings.isEmpty()) mapOf("A" to 0, "B" to 0, "C" to 0) else localServings
            }

            val lastTicketValue = if (isPro) {
                try {
                    liveSnap.child("last_ticket").value<Long?>()?.toInt() ?: 0
                } catch (t: Throwable) {
                    println("QFAST_DEBUG: ❌ Failed field 'last_ticket': $t")
                    0
                }
            } else {
                historyQueries.selectShopState(shop_id = shopId).executeAsOneOrNull()?.last_ticket?.toInt() ?: 0
            }

            val isActiveValue = try {
                liveSnap.child("is_active").value<Boolean?>() ?: false
            } catch (_: Throwable) {
                try {
                    (liveSnap.child("is_active").value<Long?>() ?: 0L) != 0L
                } catch (t: Throwable) {
                    println("QFAST_DEBUG: ❌ Failed field 'is_active': $t")
                    false
                }
            }

            val updatedAtValue = try {
                liveSnap.child("updated_at").value<Double?>()?.toLong() ?: 0L
            } catch (t: Throwable) {
                println("QFAST_DEBUG: ❌ Failed field 'updated_at': $t")
                0L
            }

            val categoriesMap = try {
                val catSnapshot = staticSnap.child("categories")
                if (catSnapshot.exists) {
                    catSnapshot.children.associate { child ->
                        (child.key ?: "") to (child.value<String?>() ?: "")
                    }.filterKeys { it.isNotBlank() }
                } else {
                    mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน")
                }
            } catch (t: Throwable) {
                println("QFAST_DEBUG: ❌ Failed field 'categories': $t")
                mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน")
            }

            val activeDeviceIdValue = try {
                liveSnap.child("active_device_id").value<String?>()
            } catch (_: Throwable) {
                null
            }

            println("QFAST_LOCK_LOG: [2. Firebase Snapshot Read] -> shopId: $shopId, active_device_id: $activeDeviceIdValue")
            println("QFAST_DEBUG:   Map Shop complete -> name: $nameValue | active: $isActiveValue | lastTicket: $lastTicketValue | plan: $planValue")

            Shop(
                id = idValue,
                name = nameValue,
                currentServings = currentServingsMap,
                lastTicket = lastTicketValue,
                isActive = isActiveValue,
                updatedAt = updatedAtValue,
                adText = try { staticSnap.child("ad_text").value<String?>() } catch (_: Throwable) { null },
                menuUrl = try { staticSnap.child("menu_url").value<String?>() } catch (_: Throwable) { null },
                openTime = try { staticSnap.child("open_time").value<String?>() } catch (_: Throwable) { null },
                closeTime = try { staticSnap.child("close_time").value<String?>() } catch (_: Throwable) { null },
                theme = try { staticSnap.child("theme").value<String?>() } catch (_: Throwable) { null },
                categories = categoriesMap,
                activeDeviceId = activeDeviceIdValue,
                plan = planValue
            )
        }
    }

    // MARK: - Queue Operations
    override fun observeActiveQueues(shopId: String): Flow<List<QueueTicket>> = flow {
        if (!isShopPro(shopId)) {
            refreshLocalQueues(shopId)
            emitAll(localQueuesFlow)
        } else {
            val ref: DatabaseReference = firebaseDatabase.reference("active_queues/$shopId")
            emitAll(
                ref.valueEvents
                    .map { snapshot: DataSnapshot ->
                        try {
                            snapshot.children.mapNotNull { child ->
                                val number = child.key?.toIntOrNull() ?: return@mapNotNull null
                                QueueTicket(
                                    number = number,
                                    category = child.child("category").value<String?>() ?: "A",
                                    status = child.child("status").value<String?>() ?: "waiting",
                                    time = child.child("time").value<String?>() ?: "",
                                    callTime = try {
                                        if (child.child("call_time").exists) {
                                            child.child("call_time").value<String?>()?.takeIf { it.isNotBlank() && it != "<null>" && it != "null" }
                                        } else null
                                    } catch (_: Throwable) {
                                        null
                                    },
                                    preOrder = child.child("pre_order").value<String?>()?.takeIf { it.isNotBlank() && it != "<null>" && it != "null" }
                                )
                            }
                        } catch (e: Exception) {
                            println("QFAST_LOG: Error mapping queues -> ${e.message}")
                            emptyList()
                        }
                    }
            )
        }
    }.catch { e ->
        println("QFAST_LOG: Firebase Flow Error (observeActiveQueues) -> ${e.message}")
    }

    override suspend fun nextQueue(shopId: String, category: String): Int? = nextQueueMutex.withLock {
        println("QFAST_MULTI_LOG: [NextQueue] -> shop: $shopId, cat: $category")
        val localDateTime = Instant.fromEpochMilliseconds(Clock.System.now().toEpochMilliseconds()).toLocalDateTime(TimeZone.currentSystemDefault())
        val hour = localDateTime.hour.toString().padStart(2, '0')
        val minute = localDateTime.minute.toString().padStart(2, '0')
        val callTimeString = "$hour:$minute"

        if (isShopPro(shopId)) {
            val activeQueuesRef = firebaseDatabase.reference("active_queues/$shopId")
            val snapshots = activeQueuesRef.valueEvents.first().children

            val nextTicketSnapshot = snapshots
                .filter {
                    it.child("status").value<String?>() == "waiting" &&
                            it.child("category").value<String?>() == category
                }
                .minByOrNull { it.key?.toIntOrNull() ?: Int.MAX_VALUE } ?: return@withLock null

            val nextNumber = nextTicketSnapshot.key!!.toInt()
            val ticketTime = nextTicketSnapshot.child("time").value<String?>() ?: ""

            withContext(Dispatchers.Default) {
                historyQueries.insertHistory(
                    shop_id = shopId,
                    ticket_number = nextNumber.toLong(),
                    served_at = Clock.System.now().toEpochMilliseconds()
                )
            }

            val updatedTicketMap = mapOf(
                "category" to category,
                "status" to "serving",
                "time" to ticketTime,
                "call_time" to callTimeString
            )
            activeQueuesRef.child(nextNumber.toString()).updateChildren(updatedTicketMap)

            val shopLiveRef = firebaseDatabase.reference("shops_live/$shopId")
            shopLiveRef.child("current_servings/$category").setValue(nextNumber)

            val previousServingTickets = snapshots.filter {
                it.key != nextNumber.toString() &&
                        it.child("category").value<String?>() == category &&
                        it.child("status").value<String?>() == "serving"
            }
            previousServingTickets.forEach { prevTicket ->
                prevTicket.key?.let { ticketKey ->
                    activeQueuesRef.child(ticketKey).removeValue()
                }
            }

            println("QFAST_MULTI_LOG: [NextQueue Success] -> $category #$nextNumber (PRO)")
            return@withLock nextNumber
        } else {
            val localQueues = historyQueries.selectActiveQueuesByShopId(shop_id = shopId).executeAsList()
            val nextTicket = localQueues
                .filter { it.status == "waiting" && it.category == category }
                .minByOrNull { it.ticket_number } ?: return@withLock null

            val nextNumber = nextTicket.ticket_number.toInt()

            withContext(Dispatchers.Default) {
                historyQueries.insertHistory(
                    shop_id = shopId,
                    ticket_number = nextNumber.toLong(),
                    served_at = Clock.System.now().toEpochMilliseconds()
                )
                historyQueries.deletePreviousServing(shop_id = shopId, category = category, ticket_number = nextNumber.toLong())
                historyQueries.updateActiveQueueServing(call_time = callTimeString, shop_id = shopId, ticket_number = nextNumber.toLong())
            }

            val currentServings = getLocalCurrentServings(shopId).toMutableMap()
            currentServings[category] = nextNumber
            saveLocalCurrentServings(shopId, currentServings)

            refreshLocalQueues(shopId)
            localShopStateTrigger.value = Clock.System.now().toEpochMilliseconds()

            println("QFAST_MULTI_LOG: [NextQueue Success] -> $category #$nextNumber (FREE Offline)")
            return@withLock nextNumber
        }
    }

    override suspend fun issueTicket(shopId: String, category: String): Int {
        println("QFAST_MULTI_LOG: [ออกตั๋วคิว] -> มีลูกค้ากดขอรับตั๋วคิวร้าน $shopId ในหมวด $category")
        val localDateTime = Instant.fromEpochMilliseconds(Clock.System.now().toEpochMilliseconds()).toLocalDateTime(TimeZone.currentSystemDefault())
        val hour = localDateTime.hour.toString().padStart(2, '0')
        val minute = localDateTime.minute.toString().padStart(2, '0')
        val currentTimeString = "$hour:$minute"

        if (isShopPro(shopId)) {
            val shopLiveRef = firebaseDatabase.reference("shops_live/$shopId")
            val newLastTicketSnapshot = shopLiveRef.child("last_ticket").runTransaction(serializer<Long?>()) { current ->
                (current ?: 0L) + 1L
            }
            val newLastTicket = newLastTicketSnapshot.value<Long?>()?.toInt() ?: 0

            val ticketMap = mapOf(
                "category" to category,
                "status" to "waiting",
                "time" to currentTimeString
            )
            firebaseDatabase.reference("active_queues/$shopId/$newLastTicket").setValue(ticketMap)
            println("QFAST_MULTI_LOG: 🎉 [ออกตั๋วสำเร็จ] -> ออกตั๋วใบใหม่หมายเลข #$newLastTicket (หมวด $category) สำเร็จ (PRO)")
            return newLastTicket
        } else {
            val currentState = historyQueries.selectShopState(shop_id = shopId).executeAsOneOrNull()
            val newLastTicket = ((currentState?.last_ticket?.toInt()) ?: 0) + 1

            if (currentState == null) {
                historyQueries.insertOrReplaceShopState(shop_id = shopId, last_ticket = newLastTicket.toLong(), current_servings_json = "{}")
            } else {
                historyQueries.updateLastTicket(last_ticket = newLastTicket.toLong(), shop_id = shopId)
            }

            historyQueries.insertActiveQueue(
                shop_id = shopId,
                ticket_number = newLastTicket.toLong(),
                category = category,
                status = "waiting",
                time = currentTimeString,
                call_time = null,
                pre_order = null
            )

            refreshLocalQueues(shopId)
            localShopStateTrigger.value = Clock.System.now().toEpochMilliseconds()
            println("QFAST_MULTI_LOG: 🎉 [ออกตั๋วสำเร็จ] -> ออกตั๋วใบใหม่หมายเลข #$newLastTicket (หมวด $category) สำเร็จ (FREE Offline)")
            return newLastTicket
        }
    }

    override suspend fun resetQueue(shopId: String) {
        println("QFAST_MULTI_LOG: ⚠️ [ระบบสิ้นวัน] -> กำลังสั่งล้างข้อมูลคิวของร้าน $shopId กลับเป็นศูนย์...")
        if (isShopPro(shopId)) {
            val shopLiveRef = firebaseDatabase.reference("shops_live/$shopId")
            shopLiveRef.child("last_ticket").setValue(0)
            shopLiveRef.child("current_servings").setValue(mapOf("A" to 0, "B" to 0, "C" to 0))
            firebaseDatabase.reference("active_queues/$shopId").removeValue()
        } else {
            historyQueries.deleteActiveQueuesByShopId(shop_id = shopId)
            historyQueries.resetShopState(shop_id = shopId)
            refreshLocalQueues(shopId)
            localShopStateTrigger.value = Clock.System.now().toEpochMilliseconds()
        }
        println("QFAST_MULTI_LOG: ✅ [รีเซ็ตสำเร็จ] -> ข้อมูลคิวร้าน $shopId ถูกรีเซ็ตเรียบร้อยแล้ว")
    }

    override suspend fun updateShopInfo(
        shopId: String,
        newName: String,
        newAdText: String,
        newMenuUrl: String,
        newOpenTime: String,
        newCloseTime: String,
        newTheme: String
    ) {
        val staticRef = firebaseDatabase.reference("shops_static/$shopId")
        val updates = mapOf(
            "name" to newName,
            "ad_text" to newAdText,
            "menu_url" to newMenuUrl,
            "open_time" to newOpenTime,
            "close_time" to newCloseTime,
            "theme" to newTheme
        )
        staticRef.updateChildren(updates)
    }

    override suspend fun updateShopCategories(shopId: String, categories: Map<String, String>) {
        val staticRef = firebaseDatabase.reference("shops_static/$shopId")
        staticRef.child("categories").setValue(categories)
    }

    override suspend fun getQueueHistory(shopId: String): List<QueueHistoryItem> {
        return withContext(Dispatchers.Default) {
            historyQueries.selectHistoryByShopId(shop_id = shopId).executeAsList().map { entity ->
                QueueHistoryItem(
                    ticketNumber = entity.ticket_number.toInt(),
                    servedAt = entity.served_at
                )
            }
        }
    }

    override suspend fun updateShopStatus(shopId: String, isActive: Boolean) {
        val liveRef = firebaseDatabase.reference("shops_live/$shopId")
        liveRef.child("is_active").setValue(isActive)
    }

    override suspend fun completeQueue(shopId: String, ticketNumber: Int) {
        if (isShopPro(shopId)) {
            val queueRef = firebaseDatabase.reference("active_queues/$shopId/$ticketNumber")
            queueRef.removeValue()
        } else {
            historyQueries.deleteActiveQueue(shop_id = shopId, ticket_number = ticketNumber.toLong())
            refreshLocalQueues(shopId)
        }
    }

    override suspend fun recallQueue(shopId: String, ticketNumber: Int) {
        val localDateTime = Instant.fromEpochMilliseconds(Clock.System.now().toEpochMilliseconds()).toLocalDateTime(TimeZone.currentSystemDefault())
        val hour = localDateTime.hour.toString().padStart(2, '0')
        val minute = localDateTime.minute.toString().padStart(2, '0')
        val callTimeString = "$hour:$minute"
        if (isShopPro(shopId)) {
            val queueRef = firebaseDatabase.reference("active_queues/$shopId/$ticketNumber")
            queueRef.child("call_time").setValue(callTimeString)
        } else {
            historyQueries.updateActiveQueueCallTime(call_time = callTimeString, shop_id = shopId, ticket_number = ticketNumber.toLong())
            refreshLocalQueues(shopId)
        }
    }

    override suspend fun skipQueue(shopId: String, ticketNumber: Int) {
        if (isShopPro(shopId)) {
            val queueRef = firebaseDatabase.reference("active_queues/$shopId/$ticketNumber")
            queueRef.child("status").setValue("skipped")
        } else {
            historyQueries.updateActiveQueueStatus(status = "skipped", shop_id = shopId, ticket_number = ticketNumber.toLong())
            refreshLocalQueues(shopId)
        }
    }

    override suspend fun createNewShop(shopId: String, shopName: String) {
        val staticRef = firebaseDatabase.reference("shops_static/$shopId")
        val defaultStatic = mapOf(
            "id" to shopId,
            "name" to shopName,
            "ad_text" to "ยินดีต้อนรับสู่ $shopName",
            "menu_url" to "",
            "open_time" to "08:00",
            "close_time" to "17:00",
            "theme" to "AMBER",
            "categories" to mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน"),
            "plan" to "FREE"
        )
        staticRef.setValue(defaultStatic)

        val liveRef = firebaseDatabase.reference("shops_live/$shopId")
        val defaultLive = mapOf(
            "is_active" to false,
            "active_device_id" to "",
            "last_ticket" to 0,
            "current_servings" to mapOf("A" to 0, "B" to 0, "C" to 0),
            "updated_at" to Clock.System.now().toEpochMilliseconds().toDouble()
        )
        liveRef.setValue(defaultLive)
        println("QFAST_MULTI_LOG: 🎉 [สร้างร้านใหม่] -> สร้างร้าน $shopName (ID: $shopId) สำเร็จ (shops_static & shops_live)")
    }

    override suspend fun deleteShop(shopId: String) {
        firebaseDatabase.reference("shops_static/$shopId").removeValue()
        firebaseDatabase.reference("shops_live/$shopId").removeValue()
        firebaseDatabase.reference("active_queues/$shopId").removeValue()
        historyQueries.deleteActiveQueuesByShopId(shop_id = shopId)
        historyQueries.resetShopState(shop_id = shopId)
    }

    override suspend fun checkShopExists(shopId: String): Boolean {
        return try {
            val staticRef = firebaseDatabase.reference("shops_static/$shopId")
            val staticSnapshot = staticRef.valueEvents.first()
            if (staticSnapshot.exists) return true

            // Backward Compatibility: คัดลอกร้านเดิมจาก shops/$shopId ไปยัง shops_static และ shops_live
            val legacyRef = firebaseDatabase.reference("shops/$shopId")
            val legacySnapshot = legacyRef.valueEvents.first()
            if (legacySnapshot.exists) {
                migrateLegacyShop(shopId, legacySnapshot)
                return true
            }
            false
        } catch (_: Throwable) {
            false
        }
    }

    private suspend fun migrateLegacyShop(shopId: String, snapshot: DataSnapshot) {
        try {
            val staticData = mapOf(
                "id" to (snapshot.child("id").value<String?>() ?: shopId),
                "name" to (snapshot.child("name").value<String?>() ?: "QFast Shop"),
                "ad_text" to (snapshot.child("ad_text").value<String?>() ?: ""),
                "menu_url" to (snapshot.child("menu_url").value<String?>() ?: ""),
                "open_time" to (snapshot.child("open_time").value<String?>() ?: "08:00"),
                "close_time" to (snapshot.child("close_time").value<String?>() ?: "17:00"),
                "theme" to (snapshot.child("theme").value<String?>() ?: "AMBER"),
                "categories" to (snapshot.child("categories").value<Map<String, String>?>() ?: mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน")),
                "plan" to (snapshot.child("plan").value<String?>()?.takeIf { it != "<null>" && it != "null" } ?: "FREE")
            )
            firebaseDatabase.reference("shops_static/$shopId").setValue(staticData)

            val liveData = mapOf(
                "is_active" to (snapshot.child("is_active").value<Boolean?>() ?: false),
                "active_device_id" to (snapshot.child("active_device_id").value<String?>() ?: ""),
                "last_ticket" to (snapshot.child("last_ticket").value<Long?>()?.toInt() ?: 0),
                "current_servings" to (snapshot.child("current_servings").value<Map<String, Int>?>() ?: mapOf("A" to 0, "B" to 0, "C" to 0)),
                "updated_at" to Clock.System.now().toEpochMilliseconds().toDouble()
            )
            firebaseDatabase.reference("shops_live/$shopId").setValue(liveData)
            println("QFAST_MULTI_LOG: 🔄 Auto-migrated legacy shop $shopId to shops_static and shops_live")
        } catch (e: Throwable) {
            println("QFAST_MULTI_LOG: ❌ Migration failed: ${e.message}")
        }
    }

    override suspend fun claimShopDevice(shopId: String, deviceId: String) {
        try {
            println("QFAST_LOCK_LOG: [1. ClaimDevice START] -> shopId: $shopId, writing deviceId: $deviceId")
            firebaseDatabase.reference("shops_live/$shopId/active_device_id").setValue(deviceId)
            println("QFAST_LOCK_LOG: [1. ClaimDevice SUCCESS] -> setValue completed")
        } catch (e: Throwable) {
            println("QFAST_LOCK_LOG: [1. ClaimDevice ERROR] -> ${e.message}")
        }
    }

    override suspend fun updatePreOrder(shopId: String, ticketNumber: Int, preOrderText: String) {
        if (!isShopPro(shopId)) return
        try {
            firebaseDatabase.reference("active_queues/$shopId/$ticketNumber/pre_order").setValue(preOrderText)
            println("QFAST_POLL_DEBUG: ✅ updatePreOrder to Firebase successful")
        } catch (e: Throwable) {
            println("QFAST_POLL_DEBUG: ❌ updatePreOrder to Firebase failed: ${e.message}")
        }
    }
}
